import React, { useMemo } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core';
import { formatCardBrand, getCardExpiryDate, getLastFour } from './paymentCardUtils';

/** Underlying payment method type returned by the API (`EpayAccount.paymentType`). */
export enum PaymentType {
  CreditCard = 'CC',
  ACH = 'ACH',
}

/**
 * Display-level payment type. For credit cards this is the card brand; for bank
 * accounts it is `ACH`. Derived from `paymentType` + `cardBrand`.
 */
export enum EPayType {
  Visa = 'VISA',
  Mastercard = 'MASTERCARD',
  AmericanExpress = 'AMERICANEXPRESS',
  Discover = 'DISCOVER',
  ACH = 'ACH',
}

interface PaymentMethodSummaryProps {
  ePayType?: string;
  accountType?: string;
  cardEnding?: string;
  goodThru?: string;
}

const useStyles = makeStyles((theme) => ({
  error: {
    color: theme.palette.error.main,
  },
}));

const PaymentMethodSummary = ({
  ePayType,
  accountType,
  cardEnding,
  goodThru,
}: PaymentMethodSummaryProps): JSX.Element => {
  const classes = useStyles();
  const isAch = ePayType?.toUpperCase() === EPayType.ACH;
  const cardBrand = formatCardBrand(ePayType);
  const lastFour = getLastFour(cardEnding);
  const cardExpiryDate = useMemo(() => getCardExpiryDate(goodThru), [goodThru]);

  if (isAch) {
    return (
      <>
        {accountType} •••• {lastFour}
      </>
    );
  }

  return (
    <>
      {cardBrand} •••• {lastFour} |{' '}
      <span className={clsx({ [classes.error]: cardExpiryDate.isExpired })}>
        {cardExpiryDate.isExpired ? 'Expired' : 'Expires'} {cardExpiryDate.expiryDate}
      </span>
    </>
  );
};

export default PaymentMethodSummary;
